// Evaluation Workspace — the reviewer surface for the Workforce framework.
//
// Three nested views held in local state (no routing needed):
//   1. Cycles list      — pick/create a cycle, start it (draft → active).
//   2. Cycle detail      — subjects table, add subjects, open one.
//   3. Subject detail    — score against the framework's factors, run the
//                          engine, then the RESULT + EXPLANATION ("show the
//                          why": factor contributions + rules fired), plus
//                          approve / lock / reopen / override — each perm-gated.
//
// docs/workforce-framework/frontend.md §Tab 3 — Evaluation Workspace.
import React, { useEffect, useMemo, useState } from 'react';
import { getApiErrorMessage } from '@/lib/apiError';
import { useTranslation } from 'react-i18next';
import {
  ArrowLeft, CheckCircle2, ClipboardList, Loader2, Lock, Plus, RotateCcw,
  Search, Sparkles, Users,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { Badge } from '@/features/shared/components/ui/badge';
import { Button } from '@/features/shared/components/ui/button';
import { Card, CardContent } from '@/features/shared/components/ui/card';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '@/features/shared/components/ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/features/shared/components/ui/table';
import { useConfirm } from '@/features/shared/components/confirm/ConfirmProvider';
import { useHasPermission } from '@/rbac/useRbac';
import {
  addSubject, approveSubject, createCycle, getCycle, getFramework, getSubject,
  isNotRolledOut, isSodConflict, listCycles, listFrameworks, lockSubject,
  overrideGrade, reopenSubject, scoreSubject, searchEmployees, startCycle, submitResponse,
  type CycleDetail, type EmployeeHit, type EvaluationCycle, type EvaluationSubject,
  type Framework, type FrameworkDetail, type SubjectDetail,
} from '@/services/workforceService';

// ------------------------------------------------------------------ helpers
const inputCls =
  'w-full h-9 px-3 rounded-md border border-border bg-card text-sm disabled:bg-muted disabled:text-muted-foreground';

const errMsg = (e: any, fallback: string) =>
  e?.response?.data?.detail || e?.response?.data?.message || e?.message || fallback;

// A fired rule's `result` is the rule's `then` outcome — an OBJECT from the engine
// (e.g. {grade:"M3"} or {ready:true}), never a bare string. Render it as readable
// text so React never receives an object as a child (was the cause of error #31).
const fmtRuleResult = (v: unknown): string => {
  if (v == null) return '';
  if (typeof v === 'object') {
    return Object.entries(v as Record<string, unknown>)
      .map(([k, val]) => `${k}: ${val}`)
      .join(', ');
  }
  return String(v);
};

const fmtDate = (iso?: string | null): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? '—'
    : d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
};

const fmtNum = (n: unknown): string =>
  typeof n === 'number' && Number.isFinite(n)
    ? Number.isInteger(n) ? String(n) : n.toFixed(2)
    : '—';

// Status → chip styling (frontend.md STATUS_META spec).
const STATUS_META: Record<string, { cls: string }> = {
  not_started: { cls: 'bg-muted text-muted-foreground border-border' },
  in_progress: { cls: 'bg-info-soft text-info-strong border-transparent' },
  pending_approval: { cls: 'bg-warning-soft text-warning-strong border-transparent' },
  approved: { cls: 'bg-teal-100 dark:bg-teal-500/[0.12] text-teal-800 dark:text-teal-300 border-teal-200 dark:border-teal-500/25' },
  locked: { cls: 'bg-success-soft text-success-strong border-transparent' },
  reopened: { cls: 'bg-orange-100 dark:bg-orange-500/[0.12] text-orange-800 dark:text-orange-300 border-orange-200 dark:border-orange-500/25' },
  draft: { cls: 'bg-muted text-muted-foreground border-border' },
  active: { cls: 'bg-info-soft text-info-strong border-transparent' },
  closed: { cls: 'bg-muted text-muted-foreground border-border' },
};

const StatusChip: React.FC<{ status?: string }> = ({ status }) => {
  const { t } = useTranslation('workforce');
  const key = status || 'not_started';
  const meta = STATUS_META[key] || STATUS_META.not_started;
  const label = t(`status_${key}`, { defaultValue: key.replace(/_/g, ' ') });
  return <Badge className={`border capitalize ${meta.cls}`}>{label}</Badge>;
};

// =========================================================== permissions ctx
interface Perms {
  canScore: boolean;
  canApprove: boolean;
  canLock: boolean;
  canReopen: boolean;
  canOverride: boolean;
}

// ================================================================ new cycle
const NewCycleDialog: React.FC<{
  frameworks: Framework[];
  onCreated: (c: EvaluationCycle) => void;
  onClose: () => void;
}> = ({ frameworks, onCreated, onClose }) => {
  const { t } = useTranslation('workforce');
  // Only published/active frameworks can back a cycle.
  const usable = useMemo(
    () => frameworks.filter((f) => f.status === 'active' || f.status === 'published'),
    [frameworks],
  );
  const [name, setName] = useState('');
  const [frameworkId, setFrameworkId] = useState('');
  const [selfDue, setSelfDue] = useState('');
  const [managerDue, setManagerDue] = useState('');
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!name.trim()) {
      toast.error(t('cycle_name_required', { defaultValue: 'Give the cycle a name.' }));
      return;
    }
    if (!frameworkId) {
      toast.error(t('framework_required', { defaultValue: 'Pick a framework.' }));
      return;
    }
    setSaving(true);
    try {
      const cycle = await createCycle({
        name: name.trim(),
        framework_id: frameworkId,
        ...(selfDue ? { self_due: selfDue } : {}),
        ...(managerDue ? { manager_due: managerDue } : {}),
      });
      toast.success(t('cycle_created', { defaultValue: 'Cycle created.' }));
      onCreated(cycle);
    } catch (e: any) {
      toast.error(getApiErrorMessage(e, errMsg(e, t('cycle_create_failed', { defaultValue: 'Could not create the cycle.' }))));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('new_cycle', { defaultValue: 'New evaluation cycle' })}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground" htmlFor="wf-cycle-name">
              {t('cycle_name', { defaultValue: 'Cycle name' })}
            </label>
            <input id="wf-cycle-name" className={inputCls} value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t('cycle_name_ph', { defaultValue: 'e.g. FY25 mid-year review' })} />
          </div>
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground" htmlFor="wf-cycle-fw">
              {t('framework', { defaultValue: 'Framework' })}
            </label>
            <select id="wf-cycle-fw" className={inputCls} value={frameworkId}
              onChange={(e) => setFrameworkId(e.target.value)}>
              <option value="">{t('select_framework', { defaultValue: 'Select a published framework…' })}</option>
              {usable.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.name}{f.current_version ? ` (v${f.current_version})` : ''}
                </option>
              ))}
            </select>
            {usable.length === 0 && (
              <p className="text-xs text-warning">
                {t('no_published_frameworks', {
                  defaultValue: 'No active/published frameworks yet — publish one before creating a cycle.',
                })}
              </p>
            )}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground" htmlFor="wf-self-due">
                {t('self_due', { defaultValue: 'Self-review due (optional)' })}
              </label>
              <input id="wf-self-due" type="date" className={inputCls} value={selfDue}
                onChange={(e) => setSelfDue(e.target.value)} />
            </div>
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground" htmlFor="wf-mgr-due">
                {t('manager_due', { defaultValue: 'Manager review due (optional)' })}
              </label>
              <input id="wf-mgr-due" type="date" className={inputCls} value={managerDue}
                onChange={(e) => setManagerDue(e.target.value)} />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>
            {t('cancel', { defaultValue: 'Cancel' })}
          </Button>
          <Button onClick={submit} disabled={saving || usable.length === 0}>
            {saving && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
            {t('create_cycle', { defaultValue: 'Create cycle' })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ============================================================== cycles list
const CyclesList: React.FC<{
  perms: Perms;
  onOpen: (cycleId: string) => void;
}> = ({ perms, onOpen }) => {
  const { t } = useTranslation('workforce');
  const [cycles, setCycles] = useState<EvaluationCycle[]>([]);
  const [frameworks, setFrameworks] = useState<Framework[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [rolloutPending, setRolloutPending] = useState(false);
  const [creating, setCreating] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    Promise.all([listCycles(), listFrameworks().catch(() => [])])
      .then(([cs, fs]) => {
        if (!alive) return;
        setCycles(cs);
        setFrameworks(fs);
      })
      .catch((e) => {
        if (!alive) return;
        if (isNotRolledOut(e)) setRolloutPending(true);
        else setLoadError(true);
      })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  const frameworkName = (id: string) => frameworks.find((f) => f.id === id)?.name || '—';

  const onStart = async (cycle: EvaluationCycle) => {
    setBusyId(cycle.id);
    try {
      const updated = await startCycle(cycle.id);
      setCycles((cur) => cur.map((c) => (c.id === cycle.id ? { ...c, status: updated.status || 'active' } : c)));
      toast.success(t('cycle_started', { defaultValue: 'Cycle started.' }));
    } catch (e: any) {
      toast.error(getApiErrorMessage(e, errMsg(e, t('cycle_start_failed', { defaultValue: 'Could not start the cycle.' }))));
    } finally {
      setBusyId(null);
    }
  };

  if (loading) {
    return (
      <div className="py-12 flex items-center justify-center text-muted-foreground">
        <Loader2 className="w-5 h-5 animate-spin mr-2" />
        {t('loading_cycles', { defaultValue: 'Loading evaluation cycles…' })}
      </div>
    );
  }
  if (rolloutPending) {
    return (
      <Card className="border-dashed">
        <CardContent className="py-14 text-center text-muted-foreground">
          <ClipboardList className="w-8 h-8 mx-auto mb-3 opacity-50" />
          <p className="text-sm">
            {t('rollout_pending', { defaultValue: 'The Workforce evaluation engine is being rolled out to your workspace. Check back soon.' })}
          </p>
        </CardContent>
      </Card>
    );
  }
  if (loadError) {
    return (
      <div className="rounded-lg px-3 py-2 text-sm border bg-danger-soft border-danger text-danger-strong">
        {t('cycles_load_error', { defaultValue: 'Could not load evaluation cycles.' })}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground">
          {t('cycles_subtitle', {
            defaultValue: 'Evaluation cycles score a set of people against a published framework. Create one, add subjects, then score and approve.',
          })}
        </p>
        {perms.canScore && (
          <Button onClick={() => setCreating(true)}>
            <Plus className="w-4 h-4 mr-1" /> {t('new_cycle', { defaultValue: 'New cycle' })}
          </Button>
        )}
      </div>

      {cycles.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="py-14 text-center text-muted-foreground">
            <ClipboardList className="w-8 h-8 mx-auto mb-3 opacity-50" />
            <p className="text-sm">
              {t('no_cycles', { defaultValue: 'No evaluation cycles yet.' })}
            </p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0 overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('col_cycle', { defaultValue: 'Cycle' })}</TableHead>
                  <TableHead>{t('col_framework', { defaultValue: 'Framework' })}</TableHead>
                  <TableHead>{t('col_status', { defaultValue: 'Status' })}</TableHead>
                  <TableHead>{t('col_created', { defaultValue: 'Created' })}</TableHead>
                  <TableHead className="text-right">{t('col_actions', { defaultValue: 'Actions' })}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {cycles.map((c) => (
                  <TableRow key={c.id} className="cursor-pointer" onClick={() => onOpen(c.id)}>
                    <TableCell>
                      <p className="text-sm font-medium text-foreground">{c.name}</p>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">{frameworkName(c.framework_id)}</TableCell>
                    <TableCell><StatusChip status={c.status} /></TableCell>
                    <TableCell className="text-xs text-muted-foreground whitespace-nowrap">{fmtDate(c.created_at)}</TableCell>
                    <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center justify-end gap-2">
                        {perms.canScore && c.status === 'draft' && (
                          <Button size="sm" variant="outline" onClick={() => onStart(c)} disabled={busyId === c.id}>
                            {busyId === c.id && <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />}
                            {t('start', { defaultValue: 'Start' })}
                          </Button>
                        )}
                        <Button size="sm" variant="ghost" onClick={() => onOpen(c.id)}>
                          {t('open', { defaultValue: 'Open' })}
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {creating && (
        <NewCycleDialog
          frameworks={frameworks}
          onClose={() => setCreating(false)}
          onCreated={(cycle) => { setCycles((cur) => [cycle, ...cur]); setCreating(false); }}
        />
      )}
    </div>
  );
};

// ============================================================= add subject
const AddSubjectDialog: React.FC<{
  cycleId: string;
  onAdded: (s: EvaluationSubject) => void;
  onClose: () => void;
}> = ({ cycleId, onAdded, onClose }) => {
  const { t } = useTranslation('workforce');
  const [ref, setRef] = useState('');
  const [label, setLabel] = useState('');
  const [saving, setSaving] = useState(false);
  // Employee directory search — fills the reference + label. Manual entry still
  // works (subject_ref stays an opaque reference; no employee PII is sent).
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<EmployeeHit[]>([]);
  const [searching, setSearching] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const term = query.trim();
    if (term.length < 1) {
      setResults([]);
      return;
    }
    setSearching(true);
    const h = setTimeout(async () => {
      try {
        setResults(await searchEmployees(term));
        setOpen(true);
      } catch {
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 250);
    return () => clearTimeout(h);
  }, [query]);

  const pick = (e: EmployeeHit) => {
    setRef(e.id);
    setLabel(e.sublabel ? `${e.name} — ${e.sublabel}` : e.name);
    setQuery(e.name);
    setOpen(false);
  };

  const submit = async () => {
    if (!ref.trim() || !label.trim()) {
      toast.error(t('subject_fields_required', { defaultValue: 'Enter both an employee ref and a label.' }));
      return;
    }
    setSaving(true);
    try {
      const s = await addSubject(cycleId, { subject_ref: ref.trim(), label: label.trim() });
      toast.success(t('subject_added', { defaultValue: 'Subject added.' }));
      onAdded(s);
    } catch (e: any) {
      toast.error(getApiErrorMessage(e, errMsg(e, t('subject_add_failed', { defaultValue: 'Could not add the subject.' }))));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('add_subject', { defaultValue: 'Add subject' })}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1 relative">
            <label className="text-xs text-muted-foreground" htmlFor="wf-subj-search">
              {t('subject_search', { defaultValue: 'Find employee' })}
            </label>
            <div className="relative">
              <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-muted-foreground" />
              <input id="wf-subj-search" className={inputCls + ' pl-8'} value={query}
                autoComplete="off"
                onChange={(e) => setQuery(e.target.value)}
                onFocus={() => results.length > 0 && setOpen(true)}
                placeholder={t('subject_search_ph', { defaultValue: 'Search by name…' })} />
              {searching && (
                <Loader2 className="w-4 h-4 animate-spin absolute right-2.5 top-2.5 text-muted-foreground" />
              )}
            </div>
            {open && results.length > 0 && (
              <div className="absolute z-20 mt-1 w-full rounded-md border border-border bg-card shadow-lg max-h-56 overflow-auto">
                {results.map((e) => (
                  <button type="button" key={e.id}
                    className="w-full text-left px-3 py-2 hover:bg-muted text-sm"
                    onClick={() => pick(e)}>
                    <div className="text-foreground">{e.name}</div>
                    {e.sublabel && <div className="text-xs text-muted-foreground">{e.sublabel}</div>}
                  </button>
                ))}
              </div>
            )}
            {open && !searching && query.trim().length > 0 && results.length === 0 && (
              <div className="absolute z-20 mt-1 w-full rounded-md border border-border bg-card shadow-lg px-3 py-2 text-xs text-muted-foreground">
                {t('subject_no_match', {
                  defaultValue: 'No match — you can still enter a reference manually below.',
                })}
              </div>
            )}
          </div>
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground" htmlFor="wf-subj-ref">
              {t('subject_ref', { defaultValue: 'Employee reference' })}
            </label>
            <input id="wf-subj-ref" className={inputCls} value={ref}
              onChange={(e) => setRef(e.target.value)}
              placeholder={t('subject_ref_ph', { defaultValue: 'e.g. employee ID or number' })} />
          </div>
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground" htmlFor="wf-subj-label">
              {t('subject_label', { defaultValue: 'Label' })}
            </label>
            <input id="wf-subj-label" className={inputCls} value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder={t('subject_label_ph', { defaultValue: 'e.g. Jane Doe — Engineering' })} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>
            {t('cancel', { defaultValue: 'Cancel' })}
          </Button>
          <Button onClick={submit} disabled={saving}>
            {saving && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
            {t('add', { defaultValue: 'Add' })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ============================================================= cycle detail
const CycleDetailView: React.FC<{
  cycleId: string;
  perms: Perms;
  onBack: () => void;
  onOpenSubject: (subject: EvaluationSubject, frameworkId: string) => void;
}> = ({ cycleId, perms, onBack, onOpenSubject }) => {
  const { t } = useTranslation('workforce');
  const [detail, setDetail] = useState<CycleDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    getCycle(cycleId)
      .then((d) => { if (alive) setDetail(d); })
      .catch(() => { if (alive) setError(true); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [cycleId]);

  if (loading) {
    return (
      <div className="py-12 flex items-center justify-center text-muted-foreground">
        <Loader2 className="w-5 h-5 animate-spin mr-2" />
        {t('loading_cycle', { defaultValue: 'Loading cycle…' })}
      </div>
    );
  }
  if (error || !detail) {
    return (
      <div className="space-y-4">
        <Button variant="ghost" size="sm" onClick={onBack}><ArrowLeft className="w-4 h-4 mr-1" />{t('back', { defaultValue: 'Back' })}</Button>
        <div className="rounded-lg px-3 py-2 text-sm border bg-danger-soft border-danger text-danger-strong">
          {t('cycle_load_error', { defaultValue: 'Could not load this cycle.' })}
        </div>
      </div>
    );
  }

  const { cycle, subjects } = detail;
  return (
    <div className="space-y-4">
      <Button variant="ghost" size="sm" onClick={onBack}><ArrowLeft className="w-4 h-4 mr-1" />{t('all_cycles', { defaultValue: 'All cycles' })}</Button>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-h3 text-charcoal flex items-center gap-2">{cycle.name} <StatusChip status={cycle.status} /></h2>
          <p className="text-sm text-muted-foreground mt-0.5">
            {t('subject_count', { defaultValue: '{{count}} subject', count: subjects.length })}
          </p>
        </div>
        {perms.canScore && (
          <Button onClick={() => setAdding(true)}>
            <Plus className="w-4 h-4 mr-1" /> {t('add_subject', { defaultValue: 'Add subject' })}
          </Button>
        )}
      </div>

      {subjects.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="py-14 text-center text-muted-foreground">
            <Users className="w-8 h-8 mx-auto mb-3 opacity-50" />
            <p className="text-sm">{t('no_subjects', { defaultValue: 'No subjects in this cycle yet.' })}</p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0 overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('col_ref', { defaultValue: 'Reference' })}</TableHead>
                  <TableHead>{t('col_label', { defaultValue: 'Subject' })}</TableHead>
                  <TableHead>{t('col_status', { defaultValue: 'Status' })}</TableHead>
                  <TableHead className="text-right">{t('col_actions', { defaultValue: 'Actions' })}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {subjects.map((s) => (
                  <TableRow key={s.id} className="cursor-pointer" onClick={() => onOpenSubject(s, cycle.framework_id)}>
                    <TableCell className="text-xs font-mono text-muted-foreground">{s.subject_ref}</TableCell>
                    <TableCell className="text-sm font-medium text-foreground">{s.label}</TableCell>
                    <TableCell><StatusChip status={s.status} /></TableCell>
                    <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                      <Button size="sm" variant="ghost" onClick={() => onOpenSubject(s, cycle.framework_id)}>
                        {t('open', { defaultValue: 'Open' })}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {adding && (
        <AddSubjectDialog
          cycleId={cycle.id}
          onClose={() => setAdding(false)}
          onAdded={(s) => {
            setDetail((cur) => (cur ? { ...cur, subjects: [...cur.subjects, s] } : cur));
            setAdding(false);
          }}
        />
      )}
    </div>
  );
};

// =========================================================== override dialog
const OverrideGradeDialog: React.FC<{
  subjectId: string;
  grades: { code: string; label: string }[];
  current?: string;
  onDone: () => void;
  onClose: () => void;
}> = ({ subjectId, grades, current, onDone, onClose }) => {
  const { t } = useTranslation('workforce');
  const [grade, setGrade] = useState(current || '');
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!grade) { toast.error(t('grade_required', { defaultValue: 'Pick a grade.' })); return; }
    if (!reason.trim()) { toast.error(t('reason_required', { defaultValue: 'A reason is required.' })); return; }
    setSaving(true);
    try {
      await overrideGrade({ subject_id: subjectId, to_value: grade, reason: reason.trim() });
      toast.success(t('override_saved', { defaultValue: 'Grade overridden.' }));
      onDone();
    } catch (e: any) {
      toast.error(getApiErrorMessage(e, errMsg(e, t('override_failed', { defaultValue: 'Could not override the grade.' }))));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('override_grade', { defaultValue: 'Override grade' })}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground" htmlFor="wf-ovr-grade">
              {t('grade', { defaultValue: 'Grade' })}
            </label>
            <select id="wf-ovr-grade" className={inputCls} value={grade} onChange={(e) => setGrade(e.target.value)}>
              <option value="">{t('select_grade', { defaultValue: 'Select a grade…' })}</option>
              {grades.map((g) => <option key={g.code} value={g.code}>{g.label || g.code}</option>)}
            </select>
          </div>
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground" htmlFor="wf-ovr-reason">
              {t('reason', { defaultValue: 'Reason (required)' })}
            </label>
            <textarea id="wf-ovr-reason" className={`${inputCls} h-20 py-2`} value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={t('override_reason_ph', { defaultValue: 'Why is the engine result being overridden?' })} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>{t('cancel', { defaultValue: 'Cancel' })}</Button>
          <Button onClick={submit} disabled={saving}>
            {saving && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
            {t('save_override', { defaultValue: 'Save override' })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ================================================= result / explanation view
const ResultView: React.FC<{ detail: SubjectDetail }> = ({ detail }) => {
  const { t } = useTranslation('workforce');
  const { result, explanation, overrides } = detail;
  if (!result && !explanation) return null;
  const total = explanation?.total_score ?? result?.total_score;
  const grade = explanation?.grade || result?.grade_code;

  return (
    <div className="space-y-4">
      {/* headline score + grade */}
      <div className="flex flex-wrap items-center gap-6 rounded-lg border border-border bg-muted/30 px-4 py-3">
        <div>
          <p className="text-xs text-muted-foreground">{t('total_score', { defaultValue: 'Total score' })}</p>
          <p className="text-h3 text-charcoal">{fmtNum(total)}</p>
        </div>
        <div>
          <p className="text-xs text-muted-foreground">{t('grade', { defaultValue: 'Grade' })}</p>
          <p className="text-h3 text-charcoal">{grade || '—'}</p>
        </div>
        {result?.status && (
          <div>
            <p className="text-xs text-muted-foreground">{t('result_status', { defaultValue: 'Result status' })}</p>
            <div className="mt-1"><StatusChip status={result.status} /></div>
          </div>
        )}
      </div>

      {/* factor contributions — the "show the why" table */}
      {explanation?.factor_contributions?.length ? (
        <div>
          <p className="text-sm font-medium text-foreground mb-1.5">
            {t('factor_contributions', { defaultValue: 'Factor contributions' })}
          </p>
          <Card>
            <CardContent className="p-0 overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('col_factor', { defaultValue: 'Factor' })}</TableHead>
                    <TableHead>{t('col_level', { defaultValue: 'Level' })}</TableHead>
                    <TableHead className="text-right">{t('col_raw', { defaultValue: 'Raw' })}</TableHead>
                    <TableHead className="text-right">{t('col_weight', { defaultValue: 'Weight' })}</TableHead>
                    <TableHead className="text-right">{t('col_weighted', { defaultValue: 'Weighted' })}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {explanation.factor_contributions.map((fc, i) => (
                    <TableRow key={`${fc.key}-${i}`}>
                      <TableCell className="text-sm font-medium text-foreground">{fc.key}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">{fc.level}</TableCell>
                      <TableCell className="text-right text-sm tabular-nums">{fmtNum(fc.raw)}</TableCell>
                      <TableCell className="text-right text-sm tabular-nums">{fmtNum(fc.weight)}</TableCell>
                      <TableCell className="text-right text-sm tabular-nums font-medium">{fmtNum(fc.weighted)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </div>
      ) : null}

      {/* rules fired */}
      {explanation?.rules_fired?.length ? (
        <div>
          <p className="text-sm font-medium text-foreground mb-1.5">
            {t('rules_fired', { defaultValue: 'Rules fired' })}
          </p>
          <div className="space-y-1.5">
            {explanation.rules_fired.map((r, i) => (
              <div key={i} className="rounded-lg border border-border bg-card px-3 py-2 text-sm">
                <span className="text-foreground">{r.explain}</span>
                {fmtRuleResult(r.result) && (
                  <span className="ml-2 text-xs font-mono text-muted-foreground">→ {fmtRuleResult(r.result)}</span>
                )}
              </div>
            ))}
          </div>
        </div>
      ) : null}

      {/* overrides trail */}
      {overrides?.length ? (
        <div>
          <p className="text-sm font-medium text-foreground mb-1.5">
            {t('overrides_trail', { defaultValue: 'Overrides' })}
          </p>
          <div className="space-y-1.5">
            {overrides.map((o, i) => (
              <div key={i} className="rounded-lg border border-orange-200 dark:border-orange-500/25 bg-orange-50 dark:bg-orange-500/[0.1] px-3 py-2 text-sm">
                <span className="text-foreground">
                  {t('override_line', {
                    defaultValue: '{{field}}: {{from}} → {{to}}',
                    field: o.field,
                    from: o.from ?? '—',
                    to: o.to,
                  })}
                </span>
                <span className="block text-xs text-muted-foreground mt-0.5">
                  {o.reason}{o.by ? ` — ${o.by}` : ''}{o.at ? ` · ${fmtDate(o.at)}` : ''}
                </span>
              </div>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
};

// ============================================================ subject detail
const SubjectDetailView: React.FC<{
  subject: EvaluationSubject;
  frameworkId: string;
  perms: Perms;
  onBack: () => void;
}> = ({ subject, frameworkId, perms, onBack }) => {
  const { t } = useTranslation('workforce');
  const confirm = useConfirm();

  const [fw, setFw] = useState<FrameworkDetail | null>(null);
  const [detail, setDetail] = useState<SubjectDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  // Scoring form state.
  const [levels, setLevels] = useState<Record<string, string>>({});
  const [comments, setComments] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [scoring, setScoring] = useState(false);
  const [busy, setBusy] = useState(false);
  const [overriding, setOverriding] = useState(false);
  const [reopening, setReopening] = useState(false);
  const [reopenReason, setReopenReason] = useState('');

  const reload = React.useCallback(async () => {
    const d = await getSubject(subject.id);
    setDetail(d);
    return d;
  }, [subject.id]);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    Promise.all([getFramework(frameworkId), getSubject(subject.id)])
      .then(([f, d]) => { if (!alive) return; setFw(f); setDetail(d); })
      .catch(() => { if (alive) setError(true); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [frameworkId, subject.id]);

  const status = detail?.subject.status || subject.status;
  const result = detail?.result;
  const isLocked = status === 'locked' || result?.status === 'locked';

  const submitResponses = async () => {
    const chosen = Object.entries(levels).filter(([, v]) => v);
    if (chosen.length === 0) {
      toast.error(t('pick_a_level', { defaultValue: 'Pick at least one factor level before submitting.' }));
      return;
    }
    setSubmitting(true);
    try {
      for (const [factorKey, levelKey] of chosen) {
        await submitResponse({
          subject_id: subject.id,
          component_key: factorKey,
          selected_level: levelKey,
          ...(comments[factorKey]?.trim() ? { comment: comments[factorKey].trim() } : {}),
        });
      }
      toast.success(t('responses_submitted', { defaultValue: 'Responses submitted.' }));
      await reload();
    } catch (e: any) {
      toast.error(getApiErrorMessage(e, errMsg(e, t('submit_failed', { defaultValue: 'Could not submit the responses.' }))));
    } finally {
      setSubmitting(false);
    }
  };

  const runScore = async () => {
    setScoring(true);
    try {
      await scoreSubject(subject.id);
      toast.success(t('scored', { defaultValue: 'Scored — see the result below.' }));
      await reload();
    } catch (e: any) {
      toast.error(getApiErrorMessage(e, errMsg(e, t('score_failed', { defaultValue: 'Could not run the scoring engine.' }))));
    } finally {
      setScoring(false);
    }
  };

  const onApprove = async () => {
    setBusy(true);
    try {
      await approveSubject(subject.id);
      toast.success(t('approved', { defaultValue: 'Result approved.' }));
      await reload();
    } catch (e: any) {
      if (isSodConflict(e)) {
        toast.error(t('sod_conflict', {
          defaultValue: 'You scored this — a different reviewer must approve.',
        }));
      } else {
        toast.error(errMsg(e, t('approve_failed', { defaultValue: 'Could not approve the result.' })));
      }
    } finally {
      setBusy(false);
    }
  };

  const onLock = async () => {
    const ok = await confirm({
      title: t('lock_title', { defaultValue: 'Lock result' }),
      message: t('lock_confirm', { defaultValue: 'Locking finalises this result. It can only change via a reopen with a reason. Continue?' }),
      confirmLabel: t('lock', { defaultValue: 'Lock' }),
    });
    if (!ok) return;
    setBusy(true);
    try {
      await lockSubject(subject.id);
      toast.success(t('locked', { defaultValue: 'Result locked.' }));
      await reload();
    } catch (e: any) {
      toast.error(getApiErrorMessage(e, errMsg(e, t('lock_failed', { defaultValue: 'Could not lock the result.' }))));
    } finally {
      setBusy(false);
    }
  };

  const onReopen = () => {
    setReopenReason('');
    setReopening(true);
  };

  const submitReopen = async () => {
    const reason = reopenReason.trim();
    if (!reason) return;
    setBusy(true);
    try {
      await reopenSubject(subject.id, reason);
      setReopening(false);
      toast.success(t('reopened', { defaultValue: 'Result reopened.' }));
      await reload();
    } catch (e: any) {
      toast.error(getApiErrorMessage(e, errMsg(e, t('reopen_failed', { defaultValue: 'Could not reopen the result.' }))));
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="py-12 flex items-center justify-center text-muted-foreground">
        <Loader2 className="w-5 h-5 animate-spin mr-2" />
        {t('loading_subject', { defaultValue: 'Loading subject…' })}
      </div>
    );
  }
  if (error || !fw || !detail) {
    return (
      <div className="space-y-4">
        <Button variant="ghost" size="sm" onClick={onBack}><ArrowLeft className="w-4 h-4 mr-1" />{t('back', { defaultValue: 'Back' })}</Button>
        <div className="rounded-lg px-3 py-2 text-sm border bg-danger-soft border-danger text-danger-strong">
          {t('subject_load_error', { defaultValue: 'Could not load this subject.' })}
        </div>
      </div>
    );
  }

  const factors = fw.definition.factors || [];
  const grades = fw.definition.grades || [];
  const hasResult = !!(detail.result || detail.explanation);

  return (
    <div className="space-y-5">
      <Button variant="ghost" size="sm" onClick={onBack}><ArrowLeft className="w-4 h-4 mr-1" />{t('back_to_cycle', { defaultValue: 'Back to cycle' })}</Button>
      <div className="flex flex-wrap items-center gap-2">
        <h2 className="text-h3 text-charcoal">{subject.label}</h2>
        <StatusChip status={status} />
        <span className="text-xs font-mono text-muted-foreground">{subject.subject_ref}</span>
      </div>

      {/* scoring form (hidden once locked) */}
      {perms.canScore && !isLocked && (
        <div className="space-y-3">
          <p className="text-sm font-medium text-foreground">{t('score_factors', { defaultValue: 'Score the factors' })}</p>
          {factors.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t('no_factors', { defaultValue: 'This framework has no factors defined.' })}</p>
          ) : (
            <div className="space-y-3">
              {factors.map((factor) => (
                <Card key={factor.key}>
                  <CardContent className="py-3 space-y-2">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <label className="text-sm font-medium text-foreground" htmlFor={`wf-lvl-${factor.key}`}>
                        {factor.label}
                      </label>
                      <select id={`wf-lvl-${factor.key}`} className={`${inputCls} sm:max-w-xs`}
                        value={levels[factor.key] || ''}
                        onChange={(e) => setLevels((cur) => ({ ...cur, [factor.key]: e.target.value }))}>
                        <option value="">{t('select_level', { defaultValue: 'Select a level…' })}</option>
                        {factor.levels.map((lv) => (
                          <option key={lv.key} value={lv.key}>
                            {lv.label}{typeof lv.score === 'number' ? ` (${lv.score})` : ''}
                          </option>
                        ))}
                      </select>
                    </div>
                    <input className={inputCls}
                      value={comments[factor.key] || ''}
                      onChange={(e) => setComments((cur) => ({ ...cur, [factor.key]: e.target.value }))}
                      placeholder={t('comment_ph', { defaultValue: 'Comment / evidence (optional)' })} />
                  </CardContent>
                </Card>
              ))}
              <div className="flex flex-wrap gap-2">
                <Button variant="outline" onClick={submitResponses} disabled={submitting || scoring}>
                  {submitting && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
                  {t('submit_responses', { defaultValue: 'Submit responses' })}
                </Button>
                <Button onClick={runScore} disabled={scoring || submitting}>
                  {scoring ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Sparkles className="w-4 h-4 mr-1" />}
                  {t('run_score', { defaultValue: 'Score' })}
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* result + explanation */}
      {hasResult && (
        <div className="space-y-4">
          <div className="h-px bg-border" />
          <p className="text-sm font-semibold text-foreground">{t('result', { defaultValue: 'Result' })}</p>
          <ResultView detail={detail} />

          {/* result actions */}
          <div className="flex flex-wrap gap-2 pt-1">
            {perms.canApprove && !isLocked && result?.status !== 'approved' && (
              <Button variant="outline" onClick={onApprove} disabled={busy}>
                <CheckCircle2 className="w-4 h-4 mr-1" /> {t('approve', { defaultValue: 'Approve' })}
              </Button>
            )}
            {perms.canLock && !isLocked && (
              <Button variant="outline" onClick={onLock} disabled={busy}>
                <Lock className="w-4 h-4 mr-1" /> {t('lock', { defaultValue: 'Lock' })}
              </Button>
            )}
            {perms.canReopen && isLocked && (
              <Button variant="outline" onClick={onReopen} disabled={busy}>
                <RotateCcw className="w-4 h-4 mr-1" /> {t('reopen', { defaultValue: 'Reopen' })}
              </Button>
            )}
            {perms.canOverride && !isLocked && (
              <Button variant="outline" onClick={() => setOverriding(true)} disabled={busy}>
                {t('override_grade', { defaultValue: 'Override grade' })}
              </Button>
            )}
          </div>
        </div>
      )}

      {overriding && (
        <OverrideGradeDialog
          subjectId={subject.id}
          grades={grades}
          current={result?.grade_code}
          onClose={() => setOverriding(false)}
          onDone={async () => { setOverriding(false); await reload(); }}
        />
      )}

      <Dialog open={reopening} onOpenChange={(o) => !o && setReopening(false)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('reopen_title', { defaultValue: 'Reopen result' })}</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <p className="text-sm text-muted-foreground">
              {t('reopen_help', {
                defaultValue:
                  'Reopening supersedes the locked result so it can be re-scored. The locked result is preserved. A reason is required.',
              })}
            </p>
            <textarea
              className={`${inputCls} h-24 py-2`}
              placeholder={t('reopen_prompt', { defaultValue: 'Reason for reopening this result:' })}
              value={reopenReason}
              onChange={(e) => setReopenReason(e.target.value)}
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setReopening(false)} disabled={busy}>
              {t('cancel', { defaultValue: 'Cancel' })}
            </Button>
            <Button onClick={submitReopen} disabled={busy || !reopenReason.trim()}>
              {busy && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />}
              {t('reopen', { defaultValue: 'Reopen' })}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ==================================================================== root
export const EvaluationWorkspaceTab: React.FC = () => {
  const perms: Perms = {
    canScore: useHasPermission('workforce:evaluation:score'),
    canApprove: useHasPermission('workforce:evaluation:approve'),
    canLock: useHasPermission('workforce:evaluation:lock'),
    canReopen: useHasPermission('workforce:evaluation:reopen'),
    canOverride: useHasPermission('workforce:override'),
  };

  const [cycleId, setCycleId] = useState<string | null>(null);
  const [subject, setSubject] = useState<{ s: EvaluationSubject; frameworkId: string } | null>(null);

  if (subject && cycleId) {
    return (
      <SubjectDetailView
        subject={subject.s}
        frameworkId={subject.frameworkId}
        perms={perms}
        onBack={() => setSubject(null)}
      />
    );
  }
  if (cycleId) {
    return (
      <CycleDetailView
        cycleId={cycleId}
        perms={perms}
        onBack={() => setCycleId(null)}
        onOpenSubject={(s, frameworkId) => setSubject({ s, frameworkId })}
      />
    );
  }
  return <CyclesList perms={perms} onOpen={setCycleId} />;
};

export default EvaluationWorkspaceTab;
